import React from 'react';
import cn from 'classnames';

import useIsScreenSize from 'js/hooks/use-is-screen-size';
import { defineTheme, type ThemeKeys } from 'js/utils/theme-helpers';

import EventImage from 'components/event-image/EventImage';
import HeightOfChild from 'components/height-of-child/HeightOfChild';
import Icon from 'components/icon/Icon';
import DatesMore from 'components/dates-more/DatesMore';
import DateField from 'components/date-field/DateField';
import Link from 'components/link/Link';

import { IDateCardDates, IDateCardDates_Dates, ILink } from 'source/NFR.types';

const themes = defineTheme({
  big: 'date-card-dates--theme-big'
});
type DateCardTheme = ThemeKeys<typeof themes>;

const hasSameValue = (dates: IDateCardDates_Dates[], key: 'year' | 'month') =>
  dates.length === 2 && dates[0][key] === dates[1][key];

interface IDateCardDatesProps extends IDateCardDates {
  heightOfName?: number;
  theme?: DateCardTheme;
  url?: string;
  setHeight?: (height: string) => void;
}

const DateCardDates: React.FC<IDateCardDatesProps> = ({
  datesTitle,
  dates = [],
  datesDescription,
  isPastDate,
  isInternational,
  isPlanned,
  labels,
  eventImage,
  className,
  theme,
  setHeight,
  moreDates,
  date,
  url,
  heightOfName
}) => {
  const setParentHeight = (height: number) => {
    if (!height || !setHeight) return;
    setHeight(`${height}px`);
  };
  const singleYear = hasSameValue(dates, 'year');
  const singleMonth = hasSameValue(dates, 'month') && singleYear;

  const isBigTheme = theme === themes.big;

  const LinkOrFragment = url ? Link : React.Fragment;
  const LinkOrFragmentProps = url ? { url, attributes: { tabIndex: '-1' } } : {};

  const isDesktop = useIsScreenSize('md');
  const style = heightOfName && isDesktop ? { minHeight: `${270 - heightOfName}px` } : {};

  return (
    <div
      className={cn('date-card-dates', className, themes(theme), {
        'date-card-dates--single-period': singleMonth || singleYear,
        'date-card-dates--light-text': eventImage,
        'date-card-dates--is-past-date': isPastDate,
        'date-card-dates--is-planned': isPlanned,
        'date-card-dates--has-more-dates': date && moreDates
      })}
      ref={node => node && setHeight && setParentHeight(node.clientHeight)}
      style={style}
    >
      <LinkOrFragment {...(LinkOrFragmentProps as ILink)}>
        {eventImage && <EventImage {...eventImage} darkFilter={eventImage.image && !!eventImage.image.src} />}

        {date && (
          <div className="date-card-dates--date">
            <DateField
              date={date}
              transparent={isPastDate || (eventImage && eventImage.image && !!eventImage.image.src)}
              theme={isBigTheme ? 'medium' : 'small'}
            />
            {isPastDate && <div className="date-card-dates--past-date">{labels.pastDate}</div>}
          </div>
        )}
      </LinkOrFragment>
      {moreDates && <DatesMore {...moreDates} theme={isBigTheme ? 'medium' : 'small'} />}
      {datesTitle && <div className="date-card-dates--description-top">{datesTitle}</div>}
      {!!dates.length && (
        <div className="date-card-dates--dates">
          {dates?.map((date, index) => (
            <div className="date-card-dates--date" key={date.day + date.month}>
              <div className="date-card-dates--day">{date.day}</div>
              {((singleMonth && !!index) || !singleMonth) && (
                <HeightOfChild>
                  {Child => (
                    <Child
                      className={cn('date-card-dates--month', {
                        '-single-period': singleMonth
                      })}
                    >
                      {date.month}
                    </Child>
                  )}
                </HeightOfChild>
              )}
              {((singleYear && !!index) || !singleYear) && (
                <HeightOfChild>
                  {Child => (
                    <Child
                      className={cn('date-card-dates--year', {
                        '-single-period': singleYear
                      })}
                    >
                      {date.year}
                    </Child>
                  )}
                </HeightOfChild>
              )}
            </div>
          ))}
        </div>
      )}
      {datesDescription && <div className="date-card-dates--description">{datesDescription}</div>}
      {isInternational && (
        <div className="date-card-dates--international">
          <Icon className="date-card-dates--international-icon" name="globe"></Icon>
        </div>
      )}
      {!isPlanned && !isInternational && !date && <div className="date-card-dates--bar" />}
    </div>
  );
};

DateCardDates.displayName = 'DateCardDates';

export default DateCardDates;
